import {
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { invariant } from "@pexip-engage/utils";
import clsx from "clsx";
import { Image, MimeType } from "remix-image";
import { ClientOnly } from "remix-utils/client-only";

import { SubjectsContainer } from "./Subject";

import { Button, ButtonProps } from "~/components/Button";
import { useEventListener } from "~/hooks/useEventListener";
import { SubjectLoaderData } from "~/routes/$enterprise+/plugin+/$sessionId.schedule.subject";
import { useSendCAEvent } from "~/routes/api+/enterprises.$enterprise.session-events";

interface SubjectCategoriesProps {
  categories: SubjectLoaderData;
}

export function SubjectGroupContainer({ categories }: SubjectCategoriesProps) {
  const firstCategory = categories[0];
  if (categories.length === 1 && firstCategory) {
    return <SubjectsContainer category={firstCategory} />;
  }

  return (
    <section
      className="grid grid-cols-1 gap-sm sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-md"
      data-component="SubjectGroupContainer"
    >
      <ClientOnly>{() => <SubjectGroupUI categories={categories} />}</ClientOnly>
    </section>
  );
}

const SubjectGroupUI = memo(function SubjectGroupUI({ categories }: SubjectCategoriesProps) {
  const { activeCategoryId, insertionIdx, setActiveElement } = useInsertionIdx();

  const activeCategory = useMemo(
    () => categories.find((category) => category.id === activeCategoryId),
    [activeCategoryId, categories],
  );

  return (
    <>
      {categories.map((category, idx) => (
        <Fragment key={category.id}>
          <SubjectGroup
            active={activeCategoryId === category.id}
            category={category}
            key={category.id}
            onPress={(e) =>
              activeCategoryId !== category.id &&
              setActiveElement({ categoryId: category.id, idx, target: e.target })
            }
          />
          {insertionIdx === idx && activeCategory && (
            <SubjectsContainer category={activeCategory} />
          )}
        </Fragment>
      ))}
    </>
  );
});

interface SubjectGroupProps extends ButtonProps {
  active: boolean;
  category: ArrayElement<SubjectLoaderData>;
}

const SubjectGroup = forwardRef<HTMLButtonElement, SubjectGroupProps>(
  ({ active, category, ...props }, ref) => {
    return (
      <Button
        {...props}
        className={clsx(
          "flex w-full flex-col",
          "rounded-lg border border-solid",
          "overflow-hidden",
          active && "border-brand bg-brand text-on-bg drop-shadow-lg",
          "hover:border-brand hover:bg-brand hover:text-on-bg",
          "focus-visible:outline-0 focus-visible:drop-shadow-lg",
          "transition-colors",
        )}
        data-active={active}
        data-component="SubjectGroup"
        data-external-id={category.externalId}
        ref={ref}
      >
        {category.image && (
          <Image
            alt={category.name}
            className="max-h-[200px] w-full flex-1 object-cover"
            data-component="SubjectGroupImage"
            loaderUrl="/api/image"
            options={{
              contentType: MimeType.WEBP,
              fit: "cover",
            }}
            placeholder="blur"
            responsive={[
              {
                size: {
                  width: 600,
                  height: 600,
                },
                maxWidth: 639,
              },
              {
                size: {
                  width: 400,
                  height: 400,
                },
                maxWidth: 768,
              },
              {
                size: {
                  width: 300,
                  height: 300,
                },
              },
            ]}
            src={category.image}
            style={{
              minHeight: "auto",
              minWidth: "auto",
            }}
          />
        )}
        <section
          className={clsx(
            "flex h-full w-full items-center justify-center p-sm text-center text-base font-bold",
          )}
          data-component="SubjectGroupTitle"
        >
          {category.name}
        </section>
      </Button>
    );
  },
);

interface SubjectGroupState {
  insertAfter: number | null;
  startIdx: number | null;
  activeCategoryId: number | null;
  activeEl: Element | null;
}

const initialState: SubjectGroupState = {
  insertAfter: null,
  startIdx: null,
  activeEl: null,
  activeCategoryId: null,
};

type Actions =
  | {
      type: "setActiveElement";
      payload: { el: Element; categoryId: number; startIdx: number };
    }
  | { type: "setInsertAfter"; payload: { insertAfter: number } }
  | { type: "resize" };

function reducer(state: SubjectGroupState, action: Actions) {
  switch (action.type) {
    case "setActiveElement":
      return {
        insertAfter: -1,
        startIdx: action.payload.startIdx,
        activeEl: action.payload.el,
        activeCategoryId: action.payload.categoryId,
      };
    case "setInsertAfter":
      return { ...state, insertAfter: action.payload.insertAfter };
    case "resize":
      return state.insertAfter === null ? state : { ...state, insertAfter: -1 };
    default:
      invariant(false, `Entered impossible state: ${JSON.stringify(action)}`);
  }
}

function useInsertionIdx() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const sendCAEvent = useSendCAEvent();

  const updateInsertAfter = useCallback(() => {
    if (state.activeEl && state.activeEl instanceof HTMLElement) {
      const insertAfter = calculateInsertAfter(state.activeEl);
      dispatch({ type: "setInsertAfter", payload: { insertAfter } });
    }
  }, [state.activeEl, dispatch]);

  useLayoutEffect(() => {
    state.insertAfter === -1 && updateInsertAfter();
  }, [state.insertAfter, updateInsertAfter]);

  const windowWidthRef = useRef(0);

  useEventListener(
    "resize",
    () => {
      if (window.innerWidth !== windowWidthRef.current) {
        windowWidthRef.current = window.innerWidth;
        dispatch({ type: "resize" });
      }
    },
    window,
  );

  const insertionIdx =
    state.insertAfter !== null &&
    state.insertAfter !== -1 &&
    state.startIdx !== null &&
    state.insertAfter + state.startIdx;

  const setActiveElement = useCallback(
    ({ categoryId, idx, target }: { categoryId: number; idx: number; target: Element }) => {
      sendCAEvent({ type: "data-selected", payload: { subtype: "subject-category", categoryId } });
      dispatch({ type: "setActiveElement", payload: { categoryId, startIdx: idx, el: target } });
    },
    [sendCAEvent],
  );

  return { setActiveElement, activeCategoryId: state.activeCategoryId, insertionIdx };
}

function calculateInsertAfter(element: HTMLElement, insertAfter = 0): number {
  const nextSibling = element.nextElementSibling;
  const isHtmlElement = nextSibling instanceof HTMLElement;

  if (!isHtmlElement) {
    return insertAfter;
  }

  if (nextSibling.offsetTop !== element.offsetTop) {
    return insertAfter;
  }

  return calculateInsertAfter(nextSibling, insertAfter + 1);
}
